import type { ContentTreeNode } from "@/lib/brand-site-content-tree";

/** 访达式浏览当前位置：根 = 静态页+栏目夹；栏目内 = 文章列表 */
export type ExplorerCwd =
  | { kind: "root" }
  | { kind: "collection"; collectionId: number; key: string; label: string };

export type ExplorerPathSegment = {
  name: string;
  cwd: ExplorerCwd;
};

export function explorerCwdKey(cwd: ExplorerCwd): string {
  if (cwd.kind === "root") return "root";
  return `collection:${cwd.collectionId}`;
}

export function sameExplorerCwd(a: ExplorerCwd, b: ExplorerCwd): boolean {
  return explorerCwdKey(a) === explorerCwdKey(b);
}

/** 当前层条目（不递归） */
export function listExplorerEntries(
  root: ContentTreeNode,
  cwd: ExplorerCwd,
): ContentTreeNode[] {
  if (cwd.kind === "root") return root.children;
  const folder = root.children.find(
    (child) =>
      child.kind === "collection_folder" && child.collectionId === cwd.collectionId,
  );
  return folder?.children ?? [];
}

/** 仅过滤当前层 */
export function filterExplorerEntries(
  entries: ContentTreeNode[],
  rawQuery: string,
): ContentTreeNode[] {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return entries;
  return entries.filter(
    (node) =>
      node.title.toLowerCase().includes(query) ||
      node.path.toLowerCase().includes(query) ||
      (node.slug?.toLowerCase().includes(query) ?? false),
  );
}

export function explorerPathSegments(cwd: ExplorerCwd): ExplorerPathSegment[] {
  const root: ExplorerPathSegment = { name: "站点", cwd: { kind: "root" } };
  if (cwd.kind === "root") return [root];
  return [
    root,
    {
      name: cwd.label || cwd.key,
      cwd: {
        kind: "collection",
        collectionId: cwd.collectionId,
        key: cwd.key,
        label: cwd.label,
      },
    },
  ];
}

export function parentExplorerCwd(cwd: ExplorerCwd): ExplorerCwd | null {
  if (cwd.kind === "root") return null;
  return { kind: "root" };
}

/** 双击文件夹 → 进入栏目 */
export function cwdFromFolderNode(node: ContentTreeNode): ExplorerCwd | null {
  if (node.kind !== "collection_folder" || node.collectionId == null || !node.slug) {
    return null;
  }
  return {
    kind: "collection",
    collectionId: node.collectionId,
    key: node.slug,
    label: node.title,
  };
}

export function isExplorerOpenable(node: ContentTreeNode): boolean {
  return node.kind === "static" || node.kind === "collection_item";
}

export function isExplorerFolder(node: ContentTreeNode): boolean {
  return node.kind === "collection_folder";
}

/**
 * 前进栈截断：从当前 index 压入新位置。
 * 返回 { stack, index }
 */
export function pushExplorerHistory(
  stack: ExplorerCwd[],
  index: number,
  next: ExplorerCwd,
): { stack: ExplorerCwd[]; index: number } {
  if (stack[index] && sameExplorerCwd(stack[index], next)) {
    return { stack, index };
  }
  const truncated = stack.slice(0, index + 1);
  truncated.push(next);
  return { stack: truncated, index: truncated.length - 1 };
}
